import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
  IconButton,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

const QuickInventoryAddForm = ({ open, onClose, operators, centers, onSubmit, theme }) => {
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [rows, setRows] = useState([{
    centerId: '',
    size: '',
    availabilityDate: moment() // Initialize with current date
  }]);
  const [filteredCenters, setFilteredCenters] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter centers when operator changes
  useEffect(() => {
    if (selectedOperator) {
      const centersByOperator = centers.filter(center => center.operatorId === selectedOperator.id);
      setFilteredCenters(centersByOperator);
      // Reset all center selections when operator changes
      setRows(rows.map(row => ({ ...row, centerId: '', centerName: '' })));
    } else {
      setFilteredCenters([]);
    }
  }, [selectedOperator, centers]);

  const handleOperatorChange = (event, newValue) => {
    setSelectedOperator(newValue);
    setRows(rows.map(row => ({ ...row, centerId: '', centerName: '' })));
  };

  const handleRowChange = (index, field, value) => {
    const newRows = [...rows];
    if (field === 'center') {
      newRows[index] = {
        ...newRows[index],
        centerId: value ? value.id : '',
        centerName: value ? value.centerName : ''
      };
    } else {
      newRows[index][field] = value;
    }
    setRows(newRows);
  };

  const addRow = () => {
    setRows([...rows, { centerId: '', size: '', availabilityDate: moment() }]);
  };

  const removeRow = (index) => {
    if (rows.length > 1) {
      const newRows = rows.filter((_, i) => i !== index);
      setRows(newRows);
    }
  };

  const resetForm = () => {
    setSelectedOperator(null);
    setRows([{
      centerId: '',
      size: '',
      availabilityDate: moment()
    }]);
    setErrors([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedOperator || rows.some(row => !row.centerId || !row.size)) {
      const newErrors = [];
      if (!selectedOperator) newErrors.push('operator');
      rows.forEach((row, index) => {
        if (!row.centerId) newErrors.push(`center-${index}`);
        if (!row.size) newErrors.push(`size-${index}`);
      });
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const today = moment().format('YYYY-MM-DD');
      
      const inventories = rows.map(row => {
        const center = centers.find(c => c.id === row.centerId);
        return {
          inventoryName: `${row.size} Seater Cabin`,
          inventoryType: 'Private Cabin',
          size: parseInt(row.size),
          centerId: row.centerId,
          centerName: center.centerName,
          operatorId: selectedOperator.id,
          operatorName: selectedOperator.operatorName,
          city: center.city,
          locality: center.locality,
          micromarket: center.micromarket,
          address: center.address,
          active: true,
          availabilityDate: row.availabilityDate.format('YYYY-MM-DD'),
          lastUpdatedDate: today,
          recordType: 'Inventory',
          recordStatus: 'UNLOCKED'
        };
      });

      await onSubmit(inventories);
      resetForm(); // Reset form after successful submission
      onClose(); // Close the dialog
    } catch (error) {
      console.error('Error submitting inventories:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Quick Add Inventories</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {errors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 3 }}>
            <Autocomplete
              value={selectedOperator}
              onChange={handleOperatorChange}
              options={operators.filter(op => op && op.id)}
              getOptionLabel={(option) => (option && option.operatorName) || ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Operator *"
                  error={!selectedOperator && errors.includes('operator')}
                  helperText={!selectedOperator && errors.includes('operator') ? 'Operator is required' : ''}
                />
              )}
              isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.operatorName}
                </li>
              )}
            />
          </FormControl>

          {rows.map((row, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Autocomplete
                value={filteredCenters.find(c => c.id === row.centerId) || null}
                onChange={(event, newValue) => handleRowChange(index, 'center', newValue)}
                options={filteredCenters.filter(center => center && center.id)}
                getOptionLabel={(option) => (option && option.centerName) || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Center *"
                    error={!row.centerId && errors.includes(`center-${index}`)}
                    helperText={!row.centerId && errors.includes(`center-${index}`) ? 'Center is required' : ''}
                  />
                )}
                disabled={!selectedOperator}
                isOptionEqualToValue={(option, value) => option && value && option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.centerName}
                  </li>
                )}
                sx={{ width: 250 }}
              />
              <TextField
                label="Size *"
                type="number"
                value={row.size}
                onChange={(e) => handleRowChange(index, 'size', e.target.value)}
                sx={{ width: 100 }}
              />
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Availability Date *"
                  value={row.availabilityDate}
                  onChange={(newValue) => handleRowChange(index, 'availabilityDate', newValue)}
                  renderInput={(params) => <TextField {...params} sx={{ width: 200 }} />}
                  sx={{ width: 200 }}
                />
              </LocalizationProvider>
              <IconButton
                onClick={() => removeRow(index)}
                sx={{ color: theme.palette.error.main }}
                disabled={rows.length === 1}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={addRow}
            sx={{ mt: 1 }}
          >
            Add Another Row
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSubmitting ? 'Adding...' : 'Add Inventories'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickInventoryAddForm;
